import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  AuthService,
  OAuthLoginError,
  ROLES_CLIENT_KEY,
  extractErrorMessage
} from '../../../../core/services/auth.service';

const EXCHANGE_TIMEOUT_MS = 10_000;

/**
 * Возврат из e-Kyzmat (/e-id/callback). Единственное место, где code меняется на сессию.
 * Со страницы всегда уходим: на успех — по роли, на ошибку — на /login с сообщением.
 */
@Component({
  selector: 'app-ekyzmat-callback',
  imports: [],
  templateUrl: './ekyzmat-callback.html',
  styleUrl: './ekyzmat-callback.css',
})
export class EkyzmatCallback implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  private translate = inject(TranslateService);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private exchangeSub: Subscription | null = null;
  private timedOut = false;

  ngOnInit(): void {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    // Закладка или F5 на чистый callback: гард сам отправит на вход, если сессии нет
    if (params.size === 0) {
      this.router.navigate(['/'], { replaceUrl: true });
      return;
    }
    if (!code || !state) {
      this.fail(this.translate.instant('login.invalidOAuthParams'));
      return;
    }

    // code одноразовый и не должен остаться в истории и Referer
    window.history.replaceState({}, document.title, window.location.pathname);

    this.timer = setTimeout(() => {
      this.timedOut = true;
      this.exchangeSub?.unsubscribe();
      this.fail(this.translate.instant('login.oauthTimeout'));
    }, EXCHANGE_TIMEOUT_MS);

    this.exchangeSub = this.authService.oauthLogin(code, state).subscribe({
      next: userResponse => {
        if (this.timedOut) return;
        this.clearTimer();
        this.navigateByRoles(userResponse.res.clientRoles?.[ROLES_CLIENT_KEY] || []);
      },
      error: error => {
        if (this.timedOut) return;
        this.fail(this.errorText(error));
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.exchangeSub?.unsubscribe();
  }

  private navigateByRoles(roles: string[]): void {
    const isStaff = ['quota_operator', 'quota_manager', 'quota_admin', 'quota_viewer']
      .some(role => roles.includes(role));
    if (isStaff) {
      this.router.navigate(['/admin'], { replaceUrl: true });
    } else if (roles.includes('quota_supplier')) {
      // guard проверит регистрацию поставщика
      this.router.navigate(['/'], { replaceUrl: true });
    } else {
      this.router.navigate(['/assign-role'], { replaceUrl: true });
    }
  }

  private errorText(error: unknown): string {
    if (error instanceof OAuthLoginError) {
      const key = {
        state: 'login.invalidOAuthParams',
        profile: 'login.oauthProfileFailed',
        'wrong-channel': 'login.oauthWrongAccount'
      }[error.reason];
      return this.translate.instant(key);
    }
    return extractErrorMessage(error, this.translate.instant('login.oauthExchangeFailed'));
  }

  private fail(message: string): void {
    this.clearTimer();
    this.authService.setAuthError(message);
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
